'use client';

import { type ELEMENT_TYPE } from 'shop-ui/constants';
import debounce from 'lodash-es/debounce';
import {
    calculateElementInfo,
    __debug_mode_hide_element_center_func,
    __debug_mode_show_element_center_func,
} from './utils';

export interface ViewportElementInfo {
    id: string;
    x: number;
    y: number;
    target: HTMLElement;
    type: (typeof ELEMENT_TYPE)[keyof typeof ELEMENT_TYPE];
}

export interface ClosestElement {
    id: ViewportElementInfo['id'];
    direction: 'top' | 'bottom' | 'left' | 'right' | null;
    name: string | null;
}

const defaultClosestElement = {
    distance: Infinity,
    id: '',
};

// const defaultClosestElementInfo = {
//     id: null,
//     rect: {
//         width: 0,
//         height: 0,
//         top: 0,
//         left: 0,
//     },
//     direction: null,
// } as const;

class ViewportElementCollector {
    _isInitialized = false;

    _debouncedInitialize: () => void;

    _elements: Map<ViewportElementInfo['id'], ViewportElementInfo>;

    _observer: Map<ViewportElementInfo['id'], IntersectionObserver>;

    _debugMode: boolean;

    _isDeleting: boolean;

    _defaultClosestElement: string | null;

    _closestElement: ClosestElement | null;

    _previousClosestElement: ClosestElement | null;

    _closestElementTriggerHandler: ((data: unknown) => void) | null;

    constructor() {
        this._elements = new Map();

        this._observer = new Map();

        this._debugMode = false;

        this._isDeleting = false;

        this._previousClosestElement = null;

        this._debouncedInitialize = debounce(this._initialize, 100);

        this._closestElement = null;

        this._defaultClosestElement = null;

        this._closestElementTriggerHandler = null;

        this._run = this._run.bind(this);
    }

    _initialize() {
        this._isInitialized = true;

        if (this._defaultClosestElement) {
            this._closestElement = {
                id: this._defaultClosestElement,
                direction: null,
                name: null,
            };

            this._previousClosestElement = this._closestElement;

            if (this._closestElementTriggerHandler) {
                this._closestElementTriggerHandler(this._closestElement);
            }
        }

        this._run();
    }

    _run() {
        if (!this._elements.size) {
            // 若有註冊事件 且正在刪除中 則觸發事件
            if (this._closestElementTriggerHandler && this._isDeleting) {
                this._closestElement = {
                    id: '',
                    direction: null,
                    name: null,
                };

                this._closestElementTriggerHandler(this._closestElement);
            }

            return;
        }

        // 更新元素資訊
        for (const [id, _info] of this._elements) {
            const { target, type } = _info;

            const info = calculateElementInfo(id, target, type);

            this._elements.set(id, info);

            if (this._debugMode) {
                __debug_mode_show_element_center_func(info);
            }
        }

        // 取得所有元素
        const viewportElements = Array.from(this._elements.values());

        // 取得視窗中心點
        const windowCenterX = window.scrollX + window.innerWidth / 2;

        const windowCenterY = window.scrollY + window.innerHeight / 2;

        // 取得最接近視窗中心點的元素
        const closestElement = viewportElements.reduce((prev, current) => {
            const { id, x: targetX, y: targetY } = current;

            // 兩點距離
            const distance = Math.sqrt(
                (windowCenterX - targetX) ** 2 + (windowCenterY - targetY) ** 2
            );

            if (distance < prev.distance) {
                return {
                    distance,
                    id,
                };
            }

            return prev;
        }, defaultClosestElement);

        // 設定最接近視窗中心點的元素
        this._closestElement = {
            id: closestElement.id,
            direction: null,
            name: null,
        };

        // 觸發事件
        // 如果最接近視窗中心點的元素與上一個不同 則觸發事件
        if (this._closestElement.id !== this._previousClosestElement?.id) {
            if (this._closestElementTriggerHandler) {
                this._closestElementTriggerHandler(this._closestElement);
            }
        }

        // 更新上一個最接近視窗中心點的元素
        this._previousClosestElement = this._closestElement;
    }

    // 設定最接近視窗中心點的元素觸發事件
    setClosestElementTriggerHandler(handler: (data: unknown) => void) {
        this._closestElementTriggerHandler = handler;
    }

    // 訂閱元素進入視窗事件
    handleIntersection(id: string, type: ViewportElementInfo['type']) {
        return (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                const { target } = entry;

                if (entry.isIntersecting) {
                    const info = calculateElementInfo(id, target as HTMLElement, type);

                    // 更新元素資訊
                    this._elements.set(id, info);

                    if (this._debugMode) {
                        // 顯示元素中心點
                        __debug_mode_show_element_center_func(info);
                    }
                } else {
                    // 刪除元素
                    this._elements.delete(id);

                    if (this._debugMode) {
                        // 隱藏元素中心點
                        __debug_mode_hide_element_center_func(id);
                    }
                }
            });
        };
    }

    // 訂閱元素進入視窗事件
    subscribe(id: string, root: Element, type: ViewportElementInfo['type']) {
        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        };

        // 如果已經訂閱過 則先取消訂閱
        if (this._observer.has(id)) {
            this.unsubscribe(id);
        }

        const handleIntersectionWithId = this.handleIntersection(id, type);

        const observer = new IntersectionObserver(handleIntersectionWithId, options);

        observer.observe(root);

        this._observer.set(id, observer);

        if (!this._isInitialized) {
            this._debouncedInitialize();

            if (!this._defaultClosestElement) {
                const rootRect = root.getBoundingClientRect();

                if (rootRect.top < window.innerHeight) {
                    this._defaultClosestElement = id;
                }
            }
        }
    }

    // 取消訂閱元素進入視窗事件
    unsubscribe(id: string, manualRun = false) {
        const observer = this._observer.get(id);

        if (observer) {
            observer.disconnect();
        }

        this._observer.delete(id);

        this._elements.delete(id);

        // 如果手動執行 則設定正在刪除中
        if (manualRun) {
            this._isDeleting = true;

            this._run();

            this._isDeleting = false;
        }
    }

    // 取得所有在視窗內的元素
    getInViewportElements() {
        return this._elements.values();
    }

    // 清除所有元素
    clear() {
        this._elements.clear();

        window.removeEventListener('scroll', this._run);

        window.removeEventListener('resize', this._run);
    }

    // 手動執行
    collect() {
        this._run();
    }

    // 開始收集
    start() {
        if (typeof window !== 'undefined' && window) {
            const params = new URLSearchParams(window.location.search);

            this._debugMode = Boolean(Number(params.get('debugMode')));

            window.addEventListener('scroll', this._run);

            window.addEventListener('resize', this._run);
        }
    }
}

export const instance = new ViewportElementCollector();
